import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PROJECT_TYPES } from '@shared/types';
import type { Guideline, ProjectType } from '@shared/types';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { Banner, EmptyState, Field, LoadingPage, Tag } from '../components/ui';
import { PageHeader } from '../components/PageHeader';

const BLANK = {
  category: '',
  title: '',
  body: '',
  keywords: '',
  projectTypes: [] as ProjectType[],
  active: true,
  sortOrder: 0,
};

export function GuidelinesPage() {
  const { isReviewer } = useAuth();
  const { notify, fail } = useToast();
  const state = useAsync(async () => api.guidelines(), []);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');

  const guidelines = state.data?.guidelines ?? [];

  const grouped = useMemo(() => {
    const filtered = query.trim()
      ? guidelines.filter((g) =>
          `${g.category} ${g.title} ${g.body} ${g.keywords.join(' ')}`
            .toLowerCase()
            .includes(query.trim().toLowerCase()),
        )
      : guidelines;

    const map = new Map<string, Guideline[]>();
    for (const guideline of filtered) {
      const list = map.get(guideline.category) ?? [];
      list.push(guideline);
      map.set(guideline.category, list);
    }
    return [...map.entries()];
  }, [guidelines, query]);

  function startEdit(guideline: Guideline) {
    setEditing(guideline.id);
    setDraft({
      category: guideline.category,
      title: guideline.title,
      body: guideline.body,
      keywords: guideline.keywords.join(', '),
      projectTypes: guideline.projectTypes,
      active: guideline.active,
      sortOrder: 0,
    });
  }

  async function save() {
    setBusy(true);
    const payload = {
      category: draft.category,
      title: draft.title,
      body: draft.body,
      keywords: draft.keywords
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k.length >= 2),
      projectTypes: draft.projectTypes,
      active: draft.active,
      sortOrder: draft.sortOrder,
    };

    try {
      const result =
        editing === 'new'
          ? await api.guidelineCreate(payload)
          : await api.guidelineUpdate(editing as string, payload);
      state.setData({ ...state.data!, guidelines: result.guidelines });
      setEditing(null);
      setDraft(BLANK);
      notify('Guideline saved.', 'success');
    } catch (error) {
      fail(error, 'The guideline was not saved.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this guideline? The AI review will stop checking against it.')) return;
    try {
      const { guidelines: next } = await api.guidelineDelete(id);
      state.setData({ ...state.data!, guidelines: next });
      notify('Guideline deleted.');
    } catch (error) {
      fail(error, 'The guideline was not deleted.');
    }
  }

  if (state.loading && !state.data) return <LoadingPage label="Loading guidelines…" />;

  return (
    <div>
      <PageHeader
        title="Architectural guidelines"
        lede={
          isReviewer
            ? 'These are the rules the AI pre-check reasons over. Keep them current and specific — vague rules produce vague reviews.'
            : 'The standards your submission will be judged against. Read the section that matches your project before you submit.'
        }
      />

      {state.data?.aiEngine === 'rules' && isReviewer && (
        <Banner kind="warn">
          No AI provider key is configured, so the AI pre-check falls back to keyword matching. A
          Super Admin can add a key in Super Admin → Settings for full review.
        </Banner>
      )}

      <div className="row" style={{ marginBottom: 14 }}>
        <Link className="btn s" to="/architecture">
          Back to submissions
        </Link>
        {isReviewer && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setEditing('new');
              setDraft(BLANK);
            }}
          >
            Add guideline
          </button>
        )}
      </div>

      <div className="card tight">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search guidelines — fence, paint, solar…"
          style={{ margin: 0 }}
        />
      </div>

      {editing && (
        <div className="card">
          <h3>{editing === 'new' ? 'New guideline' : 'Edit guideline'}</h3>
          <div className="grid two">
            <Field label="Category" hint="Groups related rules, e.g. Fences & Walls.">
              <input
                value={draft.category}
                onChange={(event) => setDraft({ ...draft, category: event.target.value })}
              />
            </Field>
            <Field label="Title">
              <input
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </Field>
          </div>

          <Field
            label="Rule text"
            hint="Write it the way it appears in your CC&Rs. The AI quotes this back to reviewers."
          >
            <textarea
              rows={6}
              value={draft.body}
              onChange={(event) => setDraft({ ...draft, body: event.target.value })}
            />
          </Field>

          <Field
            label="Keywords"
            hint="Comma separated. Used by the rules engine to spot relevant submissions."
          >
            <input
              value={draft.keywords}
              onChange={(event) => setDraft({ ...draft, keywords: event.target.value })}
              placeholder="fence, gate, privacy fence"
            />
          </Field>

          <fieldset>
            <legend>Applies to project types</legend>
            <div className="row">
              {PROJECT_TYPES.map((type) => (
                <label key={type} className="check" style={{ marginRight: 8 }}>
                  <input
                    type="checkbox"
                    checked={draft.projectTypes.includes(type)}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        projectTypes: event.target.checked
                          ? [...draft.projectTypes, type]
                          : draft.projectTypes.filter((t) => t !== type),
                      })
                    }
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="check">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(event) => setDraft({ ...draft, active: event.target.checked })}
            />
            <span>Active — include in AI reviews</span>
          </label>

          <div className="btn-row">
            <button type="button" className="btn" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save guideline'}
            </button>
            <button type="button" className="btn ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {grouped.length === 0 ? (
        <EmptyState
          title={query ? 'No guidelines match that search.' : 'No guidelines published yet.'}
          hint={isReviewer ? 'Add your CC&R sections so the AI has something to review against.' : undefined}
        />
      ) : (
        grouped.map(([category, items]) => (
          <div key={category} className="card">
            <h3>{category}</h3>
            <div className="stack" style={{ marginTop: 12 }}>
              {items.map((guideline) => (
                <div key={guideline.id} style={{ borderTop: '1px solid var(--bd)', paddingTop: 12 }}>
                  <div className="card-head">
                    <h4>{guideline.title}</h4>
                    <div className="tag-row">
                      {!guideline.active && <Tag tone="warn">Inactive</Tag>}
                      {guideline.projectTypes.map((type) => (
                        <Tag key={type}>{type}</Tag>
                      ))}
                    </div>
                  </div>
                  <p style={{ fontSize: 14, margin: '6px 0' }}>{guideline.body}</p>
                  {isReviewer && (
                    <div className="btn-row" style={{ marginTop: 6 }}>
                      <button type="button" className="btn ghost tiny" onClick={() => startEdit(guideline)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn ghost tiny"
                        onClick={() => void remove(guideline.id)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
